/* Page 1 of a class's printed set: the character sheet, drawn at the official sheet's
   measured positions. Every class shares this frame; the class data fills the header,
   Hope feature and class features, plus the play aids some features print beside them. */

import { ReactNode } from 'react';
import type { ClassFeature, DhClass } from './content/classes';
import type { SheetSection } from './content/sheetText';
import { DomainIcon } from './cards/DomainIcon';
import { Armor, classDomains, classFeatures, Weapon } from './catalog';
import { activeWeapons, Character, itemName, keptKit, markCount, Track as TrackName, TRAIT_VERBS, TRAITS } from './model';
import { AUGMENTS, IKONIS_RULES, MOTHERBOARD } from './content/motherboard';
import { augmentSlots } from './motherboard';
import { capitalize, damageLine, emphasize, FeatureText, PageProps, signed } from './SheetParts';
import { experienceList, markedTraits } from './leveling';
import { attackLine } from './beastform';
import { hopeSlots } from './companion';
import {
  Banner, Box, cutBox, Dots, EditGears, EvasionShield, focusBox, GearMark, Hands, Highlights, Html, LevelButton, PageFooter, Pill, roundBox, SheetSvg, Track, TraitShield, Txt, Value, WriteLine,
} from './svg';


/** Where each area sits, for the builder's highlight. */
const AREAS: Partial<Record<SheetSection, Box>> = {
  class: [2, 2, 196, 54],
  identity: [192, 4, 406, 45],
  evasion: [8, 70, 172, 76],
  traits: [192, 62, 412, 84],
  damage: [6, 150, 264, 122],
  hope: [6, 263, 264, 102],
  experience: [6, 356, 264, 126],
  gold: [6, 488, 264, 48],
  classFeature: [6, 538, 264, 232],
  weapons: [282, 148, 324, 226],
  armor: [282, 366, 324, 104],
  inventory: [282, 476, 324, 126],
  inventoryWeapons: [282, 598, 324, 178],
};

/** Where each area's edit gear sits. */
const GEARS: Partial<Record<SheetSection, [number, number]>> = {
  class: [186, 10],
  identity: [541, 17],
  evasion: [71.8, 80],
  traits: [601, 140],
  damage: [257, 163.5],
  experience: [257, 370.5],
  weapons: [597, 163],
  armor: [597, 379.3],
  inventory: [597, 475.2],
};

// The Level shield as the PDFs draw it: the ears behind its waist, the shield, its outline and the faint inner line.
const LEVEL_EARS = 'M552.5 24.5L590.6 24.5C591.2 25.1 591.6 25.4 592.2 26.1L592.2 38C591.6 38.6 591.2 39 590.6 39.6L552.5 39.6C551.8 39 551.5 38.6 550.8 38L550.8 26.1C551.5 25.4 551.8 25.1 552.5 24.5';
const LEVEL_SHIELD = 'M571.5 60.4C557.4 60.4 549.9 43.6 549.8 43.4L549.3 42.3L550.3 41.8C551.4 41.2 553.4 38.4 554 32.2C554.4 26.7 550.6 22.6 550.6 22.6L550.2 22.2L550.2 13.2L551.4 13.2C561.4 13.2 570.8 6.9 570.9 6.9L571.5 6.4L572.2 6.9C572.3 6.9 581.7 13.2 591.7 13.2L592.8 13.2L592.8 22.2L592.5 22.6C592.4 22.6 588.6 26.7 589.1 32.2C589.6 38.4 591.6 41.2 592.7 41.8L593.7 42.3L593.2 43.4C593.2 43.6 585.6 60.4 571.5 60.4';
const LEVEL_EDGE = 'M571.5 5.7L570.9 6.2L570.5 6.5C570.5 6.5 561.1 12.7 551.4 12.7L549.7 12.7L549.7 22.4L550 22.7L550.2 22.9C550.3 23 553.9 26.9 553.5 32.1C552.9 38.2 551 40.8 550.1 41.3L549.5 41.7L548.7 42.1L549.1 43L549.4 43.6C549.4 43.8 557.1 60.9 571.5 60.9C586 60.9 593.6 43.8 593.7 43.6L594 43L594.4 42.1L593.6 41.7L593 41.3C592 40.8 590.1 38.2 589.6 32.1C589.2 26.9 592.8 23 592.8 22.9L593.1 22.7L593.3 22.4L593.3 12.7L591.7 12.7C581.9 12.7 572.6 6.5 572.5 6.5L572.2 6.2ZM571.5 7L571.9 7.3C572 7.4 581.5 13.7 591.7 13.7L592.3 13.7L592.3 22L592.1 22.2C592.1 22.3 588.1 26.5 588.6 32.2C589.1 38.3 591 41.4 592.5 42.2L593.1 42.6L592.8 43.2C592.7 43.4 585.3 59.9 571.5 59.9C557.8 59.9 550.4 43.4 550.3 43.2L550 42.6L550.6 42.2C552 41.4 553.9 38.3 554.5 32.2C555 26.5 551 22.3 551 22.2L550.7 22L550.7 13.7L551.4 13.7C561.5 13.7 571.1 7.4 571.2 7.3ZM571.5 7';
const LEVEL_INNER = 'M571.5 56.9C561.9 56.9 555.7 47.1 553.6 43.4C555.6 41.2 556.9 37.4 557.4 32.5C557.8 26.8 554.9 22.5 553.6 20.9L553.6 16.6C561.6 15.9 568.6 12.2 571.5 10.5C574.5 12.2 581.5 15.9 589.4 16.6L589.4 20.9C588.1 22.5 585.2 26.8 585.7 32.5C586.1 37.4 587.5 41.2 589.4 43.4C587.4 47.1 581.1 56.9 571.5 56.9ZM571.5 56.9';

const PILL_X = [197.5, 265.8, 332.2, 398.8, 467.1, 533.6];
const COIN_X = [15.5, 27.8, 40.1, 52.7, 65.1, 77.9, 90.2, 102.6, 115.2];
const SLOT_X = [135.7, 148.8, 161.8];
const SLOT_Y = [78.7, 93.1, 107.5, 121.8];

export default function SheetPage({ c, catalog, stats, highlight, closeUp, onMark, onEdit, onChoose, blank }: PageProps) {
  const cls = catalog.cls(c.className);
  const domains = classDomains(cls);
  const mark = (track: TrackName) => (onMark ? (n: number) => onMark(track, n) : undefined);
  const heritage = [c.community, [c.ancestry, c.mixedAncestry].filter(Boolean).join('-')].filter(Boolean).join(' ');
  const { primary, secondary } = activeWeapons(c, catalog);
  const hope = hopeSlots(c, catalog);
  const name = cls?.name ?? 'Daggerheart';
  const marked = blank?.includes('Trait marks') ? [] : markedTraits(c);
  const experiences = blank?.includes('Experience') ? [] : experienceList(c);
  // Left empty for the PDF, the armor box and its numbers stay blank even when a card (Bare Bones) would fill them.
  const armorBlank = blank?.includes('Active armor');
  const armorScore = armorBlank ? null : stats.armorScore;

  return (
    <SheetSvg label="Character sheet" className="cs-sheet" view={closeUp ? focusBox(AREAS, highlight) : undefined}>
      {/* ── Header ── */}
      <rect className="s-tint" x={0} y={0} width={612} height={50} />
      <path className="s-band" d="M0 0H203.9L174.4 57.1H0Z" />
      <Html x={12} y={name.length > 9 ? 11.7 : 8.8} w={168} h={43} className="cs-classhead">
        <div className="cs-classhead__name" style={{ fontSize: name.length > 9 ? 15 : 19 }}>
          {name}
          {domains.map((d) => <DomainIcon key={d} domain={d} className="cs-classhead__icon" />)}
        </div>
        <div className="cs-classhead__domains">{cls ? domains.join(' & ') : 'Character Sheet'}</div>
      </Html>
      <Field box={[206.1, 7.5, 396.0, 26.0]} label="NAME" value={c.name} />
      <Field box={[399.7, 7.5, 533.7, 26.0]} label="PRONOUNS" value={c.pronouns} />
      <Field box={[195.7, 27.4, 366.3, 45.9]} label="HERITAGE" value={heritage} />
      <Field box={[369.7, 27.4, 533.7, 45.9]} label="SUBCLASS" value={c.subclass} />
      <path className="s-frame" d={LEVEL_EARS} />
      <path className="s-paper" d={LEVEL_SHIELD} />
      <path className="s-frame" d={LEVEL_EDGE} />
      <path className="o-faint o-thin" d={LEVEL_INNER} />
      <Txt x={571.5} y={38} size={15} weight={800} anchor="middle">{blank?.includes('Level') ? '' : stats.level}</Txt>
      <Pill x={545.7} y={46.6} w={51.6} h={9.6} label="LEVEL" size={7.7} />

      {/* ── Evasion, Armor and Armor Slots ── */}
      <EvasionShield value={blank?.includes('Evasion') ? '' : stats.evasion ?? ''} start={cls?.stats?.evasion} />
      <line className="o-frame o-thin" x1={71.8} y1={90.7} x2={71.8} y2={121} />
      {/* The Armor shield as the PDFs draw it: the plate behind, the shield on its outline's centre line, the faint inner line */}
      <path className="s-frame" d="M87.3 93.5L123.9 93.5C124.6 94.4 125 94.9 125.8 95.9L130.7 102.6L127.7 114.7C127 115.7 123.6 116.2 122.9 117.1L88.3 117.1C87.5 116.2 84.2 115.7 83.4 114.7L80.5 102.6L85.4 95.9C86.1 94.9 86.5 94.4 87.3 93.5Z" />
      <path className="s-paper o-frame" d="M103.2 131.2L98.4 128.9C91.9 124.8 86.9 117.7 85.1 109.8L81.5 94.6L93.1 78.2L93.6 78.7C96.8 81.6 101 83.1 105.5 83.1C109.9 83.1 114.1 81.6 117.3 78.7L117.8 78.2L129.4 94.6L125.8 109.8C124 117.7 119 124.8 112.6 128.9L107.7 131.2L105.4 132.4Z" />
      <path className="o-faint o-thin" d="M100.1 125.1C94.1 121.2 89.9 115.5 88.3 109.1L85.1 95.3L93.8 83C96.3 85.9 102.6 87.1 102.6 87.1L105.5 85.5L108.5 87.1C108.5 87.1 114.6 85.6 117.1 83L125.8 95.3L122.6 109.1C121.1 115.5 116.9 121.2 110.8 125.1L105.5 128.4Z" />
      <Txt x={105.5} y={108} size={18} weight={800} anchor="middle">{armorScore ?? ''}</Txt>
      <Pill x={79.8} y={116.9} w={51.6} h={9.7} label="ARMOR" size={7.7} />
      <Track
        label="Armor Slots"
        total={12}
        open={armorScore ?? 12}
        marked={markCount(c, 'armor')}
        onSet={mark('armor')}
        slot={(i) => (
          <path
            className="shape"
            transform={`translate(${SLOT_X[i % 3]} ${SLOT_Y[Math.floor(i / 3)]})`}
            d="M2.8 0.4Q5.4 1.5 8 0.4L10.6 3.7L9.9 7Q8.4 10.3 6.7 11.4L5.4 12L4.1 11.4Q2.4 10.3 0.9 7L0.2 3.7Z"
          />
        )}
      />
      <line className="o-frame" x1={187.5} y1={71.1} x2={187.5} y2={144.5} />

      {/* ── Beastform: lit over the traits while the Druid is transformed ── */}
      {stats.beastform && (
        <Html x={192} y={56.8} w={410} h={11} size={7} className="cs-beastform" fit>
          <strong>BEASTFORM: {stats.beastform.label.toUpperCase()}</strong>
          <span>
            {[
              `${capitalize(stats.beastform.trait)} +${stats.beastform.traitBonus}`,
              c.beastform?.evolution && `${capitalize(c.beastform.evolution)} +1 (Evolution)`,
              `Evasion +${stats.beastform.evasionBonus}`,
              attackLine(stats.beastform, stats.proficiency),
            ].filter(Boolean).join(' · ')}
          </span>
        </Html>
      )}

      {/* ── Traits ── */}
      {TRAITS.map((t, i) => (
        <g key={t}>
          <TraitShield x={PILL_X[i]} label={t.toUpperCase()} value={blank?.includes('Traits') ? '' : signed(stats.traits[t])} verbs={TRAIT_VERBS[t]} marked={marked.includes(t)} />
          {i > 0 && <Dots x={PILL_X[i] - 3.6} y0={85.4} y1={140.7} />}
        </g>
      ))}

      {/* ── Damage & Health ── */}
      <path className="o-frame" d={roundBox(-18.6, 163.5, 270.8, 274.7, 2.8, true)} />
      <Banner x0={41} x1={230.4} y={155.9} title="DAMAGE & HEALTH" />
      <Txt x={137.95} y={180.8} size={6.4} tone="soft" anchor="middle">Add your current level to your damage thresholds.</Txt>
      <rect className="o-frame" x={76.1} y={190.4} width={156.9} height={17} />
      {[0, 1, 2].map((k) => (
        <Threshold key={k} x={16.2 + k * 88.1} label={['MINOR', 'MAJOR', 'SEVERE'][k]} hp={k + 1} arrow={k > 0} />
      ))}
      <Txt x={93.9} y={202.6} size={11} weight={800} anchor="middle">{armorBlank ? '' : stats.major ?? ''}</Txt>
      <Txt x={182} y={202.6} size={11} weight={800} anchor="middle">{armorBlank ? '' : stats.severe ?? ''}</Txt>
      <Txt x={18} y={237.5} size={9.1} weight={900} tone="label">HP</Txt>
      <Track label="Hit Points" total={12} open={stats.hp} marked={markCount(c, 'hp')} onSet={mark('hp')}
        slot={(i) => <rect className="shape" x={37.5 + i * 18.8} y={231.1} width={14.3} height={6.6} rx={1.2} />} />
      <Txt x={18.3} y={259.8} size={9.1} weight={900} tone="label">STRESS</Txt>
      <Track label="Stress" total={12} open={stats.stress} marked={markCount(c, 'stress')} onSet={mark('stress')}
        slot={(i) => <rect className="shape" x={63.4 + i * 16.5} y={253.1} width={12.4} height={6.7} rx={1.2} />} />

      {/* ── Hope ── */}
      <path className="o-frame" d={roundBox(-33.4, 276.8, 270.4, 368, 2.8, true)} />
      <Banner x0={41} x1={230.4} y={268.4} title="HOPE" />
      <Txt x={136.4} y={294.8} size={7} tone="soft" anchor="middle">
        Spend a Hope to {cls?.source === 'HF' ? 'utilize' : 'use'} an experience or help an ally.
      </Txt>
      <path className="s-hopebar" d="M34.3 301.1H237.1L243.7 307.6V311.4L237.1 318H34.3L27.8 311.4V307.6Z" />
      {[0, 1, 2, 3, 4].map((k) => <line key={k} className="o-paper" x1={64.8 + k * 35.45} y1={305.8} x2={64.8 + k * 35.45} y2={313.2} />)}
      {/* Light in the Dark's slot gets its own short bar past the end of the printed one. */}
      {hope > 6 && <path className="s-hopebar" d="M252.5 301.1H261.5L266.5 307.6V311.4L261.5 318H252.5L247.5 311.4V307.6Z" />}
      <Track label="Hope" total={hope} marked={markCount(c, 'hope')} onSet={mark('hope')}
        slot={(i) => {
          const x = i < 6 ? 42.06 + i * 35.45 : 252;
          return <rect className="shape shape--diamond" x={x} y={304.5} width={10} height={10} rx={1.2} transform={`rotate(45 ${x + 5} 309.5)`} />;
        }} />
      {cls?.hopeFeature && (
        <Html x={14} y={328.6} w={244} h={31} size={8} className="cs-center" fit>
          <FeatureText text={cls.hopeFeature} />
        </Html>
      )}

      {/* ── Experience ── */}
      <path className="o-frame" d={roundBox(-33.4, 370.5, 270.4, 484.4, 2.8, true)} />
      <Banner x0={41} x1={230.4} y={361.4} title="EXPERIENCE" />
      {[0, 1, 2, 3, 4].map((i) => {
        const y = 392.6 + i * 19.65;
        const exp = experiences[i];
        return (
          <g key={i}>
            <path className="o-faint" d={`M15.5 ${y + 2.2}L18 ${y + 5.3}H213.1`} />
            <path className="s-tint" d={`M254.5 ${y}L250.7 ${y + 3.8}H228.1Q226.2 ${y} 224.3 ${y}H220.9L215.3 ${y + 5.7}H258.2V${y}Z`} />
            <Value x={19} base={y + 4.4} w={194}>{exp?.name}</Value>
            {exp?.name && <Txt x={238} y={y + 2.4} size={8.5} weight={900} anchor="middle">{signed(exp.bonus)}</Txt>}
          </g>
        );
      })}

      {/* ── Gold ── */}
      <path className="o-frame" d={roundBox(-28, 491.6, 270.4, 534.1, 2.8)} />
      <Txt x={135.75} y={503.7} size={9.1} weight={900} tone="title" anchor="middle">GOLD</Txt>
      <Track label="Handfuls" total={9} marked={markCount(c, 'handfuls')} onSet={mark('handfuls')}
        slot={(i) => <Coin x={COIN_X[i]} />} />
      <Dots x={129.3} y0={509.8} y1={529.1} tone="rule" />
      <Track label="Bags" total={9} marked={markCount(c, 'bags')} onSet={mark('bags')}
        slot={(i) => (
          <path className="shape shape--thin" transform={`translate(${133.8 + i * 11.25} 510.7)`}
            d="M4 12.8Q0.4 12.6 0.4 8.4Q1.4 5.8 3.1 4.9L3.9 4.1L2.7 2.5Q3.6 0.8 5.1 0.2Q6.6 0.8 7.5 2.5L6.3 4.1L7.1 4.9Q8.8 5.8 9.8 8.4Q9.8 12.6 6.2 12.8Z" />
        )} />
      <Dots x={236.7} y0={509.8} y1={529.1} tone="rule" />
      <Track label="Chest" total={1} marked={markCount(c, 'chest')} onSet={mark('chest')}
        slot={() => (
          <g>
            <path className="shape shape--chest" d="M243.3 513.3Q243.6 509.4 250.1 509.2Q256.6 509.4 256.9 513.3V523.1H243.3Z" />
            <rect className="s-paper o-hopebar" x={245.9} y={514} width={8.4} height={8.4} />
          </g>
        )} />
      <Txt x={15.2} y={529.1} size={5.3} weight={800}>HANDFULS</Txt>
      <Txt x={133.4} y={529.1} size={5.3} weight={800}>BAGS</Txt>
      <Txt x={242.8} y={529.1} size={5.3} weight={800}>CHEST</Txt>

      {/* ── Class feature ── */}
      <path className="o-frame" d="M195.3 763.7L189.4 757.9H82.8L76.9 763.7H-23.5L-28.5 758.7V560.9L-23.5 555.9H26.9L32.8 561.8H239.4L245.3 555.9H264.5L266.2 557.7V762L264.5 763.7Z" />
      <path className="o-frame" d="M33.8 556.5L29 551.7H-23.8Q-28 551.7 -28 555.9V763.7Q-28 767.9 -23.8 767.9H80.8Q85 767.9 85 763.7" />
      <path className="o-frame" d="M186.4 763.7Q186.4 767.9 190.6 767.9H266.8Q270.4 767.9 270.4 764.3V555.2Q270.4 551.7 267 551.7H242.2L237.4 556.5" />
      <Banner x0={41} x1={230.4} y={544.4} title="CLASS FEATURE" tabRaise={3.1} tabDrop={-2} tabCut={5.1} />
      <Html x={18.3} y={567.6} w={241} h={189} size={8} className="cs-features" fit>
        {classFeatures(cls).map((f, i) => <ClassFeatureBlock key={f.name ?? i} feature={f} c={c} onMark={onMark} onChoose={onChoose} />)}
      </Html>

      {/* ── Active weapons ── */}
      <path className="o-frame" d={roundBox(278, 163, 669.1, 376.9, 2.8, true)} />
      <path className="s-frame o-frame" d="M380.3 154.2H508.7Q521.5 154.2 521.5 167Q521.5 179.8 508.7 179.8H380.3Q367.5 179.8 367.5 167Q367.5 154.2 380.3 154.2Z" />
      <path className="s-paper o-frame" d="M380.3 156.5H508.7L521.5 169.3V172.1L508.7 184.9H380.3L367.5 172.1V169.3Z" />
      <Banner x0={347.2} x1={536.6} y={155.5} title="ACTIVE WEAPONS" tab={null} wings={[342.8, 581.1]} />
      {!c.useIkonis && <Hands x={540.3} y={150.6} used={handsUsed(primary, secondary)} />}
      <Txt x={383.8} y={179.8} size={7} weight={900} tone="title">PROFICIENCY</Txt>
      <Track label="Proficiency" total={6} marked={stats.proficiency}
        slot={(i) => (
          <g>
            <circle className="s-paper o-frame o-thin" cx={443.95 + i * 11.45} cy={176.95} r={4.7} />
            <circle className="dot" cx={443.95 + i * 11.45} cy={176.95} r={3.25} />
          </g>
        )} />
      {c.useIkonis ? <IkonisLines c={c} weapon={primary} /> : (
        <>
          <WeaponLines top={0} titleBase={193.3} title="PRIMARY" weapon={primary} trait={c.strikeTrait && capitalize(c.strikeTrait)} />
          <WeaponLines top={93.3} titleBase={285.8} title="SECONDARY" weapon={secondary} />
        </>
      )}

      {/* ── Active armor ── */}
      <path className="o-frame" d={roundBox(278, 379.3, 669.1, 472.8, 3.2)} />
      <Banner x0={348.1} x1={537.5} y={370.6} title="ACTIVE ARMOR" tab={[395.8, 489.8]} />
      <ArmorLines armor={armorBlank ? undefined : stats.armorBox} />

      {/* ── Inventory ── */}
      <path className="o-frame" d={roundBox(278, 475.2, 669.1, 781.4, 2.8)} />
      <Txt x={442.55} y={490.3} size={9.1} weight={900} tone="title" anchor="middle">INVENTORY</Txt>
      {inventoryLines(c, cls, blank?.includes('Inventory')).map((item, i) => {
        const y = 503.7 + i * 20.22;
        return (
          <g key={i}>
            <WriteLine x0={290.2} x1={595.5} y={y} tone="faint" />
            <Value x={296} base={y + 2.2} w={296} size={8}>{item}</Value>
          </g>
        );
      })}
      <StoredWeapon top={0} weapon={catalog.weapon(c.inventoryWeapons[0] ?? '')} />
      <StoredWeapon top={90.5} weapon={catalog.weapon(c.inventoryWeapons[1] ?? '')} />

      <PageFooter hf={cls?.source === 'HF'} />
      <Highlights boxes={AREAS} on={highlight} />
      <EditGears at={GEARS} onEdit={onEdit} />
      {onEdit && stats.level < 10 && <LevelButton x={603} y={33} onClick={() => onEdit('level')} />}
    </SheetSvg>
  );
}

function Field({ box: [x0, y0, x1, y1], label, value }: { box: [number, number, number, number]; label: string; value?: string }) {
  return (
    <g>
      <path className="s-paper o-frame o-thin" d={cutBox(x0, y0, x1, y1, 2.4)} />
      <Txt x={x0 + 3.4} y={y0 + 6.8} size={5.7} weight={900} tone="muted">{label}</Txt>
      <Value x={x0 + 3.4} base={y1 - 1} w={x1 - x0 - 7} size={7.8}>{value}</Value>
    </g>
  );
}

function Threshold({ x, label, hp, arrow }: { x: number; label: string; hp: number; arrow: boolean }) {
  const p = (dx: number, y: number) => `${x + dx} ${y}`;
  return (
    <g>
      <path
        className="s-frame o-paper"
        d={`M${p(0.9, 185.1)}H${x + 5.9}L${p(7.4, 186.6)}H${x + 60}L${p(61.5, 185.2)}H${x + 66.5}L${p(67.3, 186)}V191L${p(65.7, 192.5)}V205.2L${p(67.2, 206.8)}V211.8L${p(66.4, 212.5)}H${x + 61.4}L${p(59.9, 211)}H${x + 7.3}L${p(5.8, 212.4)}H${x + 0.8}L${p(0, 211.6)}V206.6L${p(1.6, 205.1)}V192.4L${p(0.1, 190.9)}V185.9Z`}
      />
      {arrow && (
        <>
          <path className="s-paper" d={`M${p(1.4, 191.4)}L${p(8.8, 198.8)}L${p(1.4, 206.2)}Z`} />
          <path className="s-frame" d={`M${p(1.8, 194.9)}L${p(5.6, 198.8)}L${p(1.8, 202.7)}Z`} />
        </>
      )}
      <Txt x={x + 33.65} y={196.5} size={7} weight={900} tone="frame" anchor="middle">{label}</Txt>
      <Txt x={x + 33.65} y={205.4} size={7} weight={900} tone="frame" anchor="middle">DAMAGE</Txt>
      <Txt x={x + 33.65} y={217.7} size={6.4} tone="soft" anchor="middle">Mark {hp} HP</Txt>
    </g>
  );
}

function Coin({ x }: { x: number }) {
  const cx = x + 3.85;
  return (
    <g>
      <path className="shape shape--thin shape--edge" d={`M${cx + 2.6} 513.3A3.85 3.85 0 0 1 ${cx + 2.6} 521`} />
      <path className="shape shape--thin shape--edge" d={`M${cx + 1.6} 513.3A3.85 3.85 0 0 1 ${cx + 1.6} 521`} />
      <circle className="shape shape--thin" cx={cx} cy={517.15} r={3.85} />
    </g>
  );
}

const handsUsed = (primary?: Weapon, secondary?: Weapon) =>
  (primary?.burden === 'Two-Handed' ? 2 : primary?.burden === 'One-Handed' ? 1 : 0) + (secondary ? 1 : 0);

/** Name, trait & range and damage lines with the feature lines under them. */
/** One active weapon's lines; `titleBase` is the heading's baseline, which the PDF sets apart from the block's offset. */
function WeaponLines({ top, titleBase, title, weapon, trait }: { top: number; titleBase: number; title: string; weapon?: Weapon; trait?: string }) {
  const y = (v: number) => v + top;
  return (
    <g>
      <Txt x={290.1} y={titleBase} size={9.1} weight={900} tone="title">{title}</Txt>
      <Dots x={415.5} y0={y(200.8)} y1={y(208.1)} tone="rule" />
      <Dots x={511.1} y0={y(200.8)} y1={y(208.1)} tone="rule" />
      <WriteLine x0={290.2} x1={595.5} y={y(209.2)} />
      <Value x={297} base={y(210.6)} w={116}>{weapon?.name}</Value>
      <Value x={417} base={y(210.6)} w={92}>{weapon && [weapon.trait || trait, weapon.range].filter(Boolean).join(' ')}</Value>
      <Value x={513} base={y(210.6)} w={80}>{weapon && damageLine(weapon)}</Value>
      <Txt x={297.1} y={y(220.1)} size={6.4} weight={800}>NAME</Txt>
      <Txt x={416.2} y={y(220.1)} size={6.4} weight={800}>TRAIT &amp; RANGE</Txt>
      <Txt x={513.1} y={y(220.1)} size={6.4} weight={800}>DAMAGE DICE &amp; TYPE</Txt>
      <FeatureLines top={y(234.8)} text={weapon?.feature} />
    </g>
  );
}

const AUGMENT_ROWS = [302.5, 318.5, 334.5];
const AUGMENT_COLS = [[290.2, 440.5], [446, 595.5]];

/**
 * An Ikonis takes the whole Active Weapons box (only Proficiency stays), built like the primary weapon:
 * name, trait and range, damage, then its description and Bonded, then the installed augments (locked
 * slots faint) and the relics, condensed. Full augment texts are on the Motherboard Module page.
 */
function IkonisLines({ c, weapon }: { c: Character; weapon?: Weapon }) {
  const slots = augmentSlots(c);
  const label = (x: number, y: number, text: string) => <Txt x={x} y={y} size={6.4} weight={800}>{text}</Txt>;
  return (
    <g>
      <Txt x={290.1} y={193.5} size={9.1} weight={900} tone="title">IKONIS</Txt>
      <Txt x={595.5} y={193.5} size={6.4} weight={800} tone="muted" anchor="end">TWO-HANDED</Txt>
      <Dots x={415.5} y0={200.8} y1={208.1} tone="rule" />
      <Dots x={511.1} y0={200.8} y1={208.1} tone="rule" />
      <WriteLine x0={290.2} x1={595.5} y={209.2} />
      <Value x={297} base={210.6} w={116}>{c.ikonis.name}</Value>
      <Value x={417} base={210.6} w={92}>{weapon && [weapon.trait, weapon.range].filter(Boolean).join(' ')}</Value>
      <Value x={513} base={210.6} w={80}>{weapon && damageLine(weapon)}</Value>
      {label(297.1, 220.1, 'NAME')}
      {label(416.2, 220.1, 'TRAIT & RANGE')}
      {label(513.1, 220.1, 'DAMAGE DICE & TYPE')}
      <WriteLine x0={290.2} x1={595.5} y={234.8} />
      <Value x={297} base={236.2} w={296} size={8}>{c.ikonis.description}</Value>
      {label(297.1, 245.7, 'DESCRIPTION')}
      <Value x={297} base={260.2} w={296} size={8.5}>
        <strong><em>{MOTHERBOARD.bonded.name}:</em></strong> {MOTHERBOARD.bonded.text}
      </Value>
      <WriteLine x0={290.2} x1={595.5} y={261.4} />
      {label(297.1, 272.3, 'FEATURE')}
      <Txt x={290.1} y={288.5} size={7.5} weight={900} tone="title">INSTALLED AUGMENTS</Txt>
      <Txt x={595.5} y={288.5} size={6.4} weight={800} tone="muted" anchor="end">{slots} SLOTS</Txt>
      {Array.from({ length: 5 }, (_, i) => {
        const [x0, x1] = AUGMENT_COLS[Math.floor(i / 3)];
        const y = AUGMENT_ROWS[i % 3];
        const augment = AUGMENTS.find((a) => a.name === c.ikonis.installed[i]);
        return (
          <g key={i}>
            <WriteLine x0={x0} x1={x1} y={y} tone={i < slots ? 'rule' : 'faint'} />
            <Value x={x0 + 6} base={y + 1.4} w={x1 - x0 - 12} size={7.5}>{augment && `${augment.name}: ${augment.text}`}</Value>
          </g>
        );
      })}
      {label(297.1, 357.4, 'RELICS')}
      <WriteLine x0={327.2} x1={595.5} y={356.2} />
      <Value x={331} base={357.6} w={262} size={7.5}>{c.ikonis.relics.filter(Boolean).join(', ')}</Value>
    </g>
  );
}

/** Two feature lines: the first full width, the second after the FEATURE label. */
function FeatureLines({ top, text }: { top: number; text?: string | null }) {
  return (
    <g>
      <WriteLine x0={290.2} x1={595.5} y={top} />
      <Txt x={298.1} y={top + 10.9} size={6.4} weight={800}>FEATURE</Txt>
      <WriteLine x0={327.2} x1={595.5} y={top + 23.3} />
      {text && (
        <Html x={296} y={top - 12.6} w={298} h={38} size={7.5} className="cs-feature-lines" fit>
          <FeatureText text={text} />
        </Html>
      )}
    </g>
  );
}

/** The Active Armor box: the worn armor, or a no-armor card's base thresholds and score (Bare Bones). */
function ArmorLines({ armor }: { armor?: Pick<Armor, 'name' | 'major' | 'severe' | 'score' | 'feature'> }) {
  return (
    <g>
      <Dots x={450.1} y0={398.1} y1={405.4} tone="rule" />
      <Dots x={533.1} y0={398.1} y1={405.4} tone="rule" />
      <WriteLine x0={290.2} x1={595.5} y={406.5} />
      <Value x={297} base={407.9} w={150}>{armor?.name}</Value>
      <Value x={456} base={407.9} w={74}>{armor && `${armor.major} / ${armor.severe}`}</Value>
      <Value x={538} base={407.9} w={55}>{armor?.score}</Value>
      <Txt x={297.1} y={417.4} size={6.4} weight={800}>NAME</Txt>
      <Txt x={455.1} y={417.4} size={6.4} weight={800}>BASE THRESHOLDS</Txt>
      <Txt x={537.1} y={417.4} size={6.4} weight={800}>BASE SCORE</Txt>
      <FeatureLines top={433.1} text={armor?.feature} />
    </g>
  );
}

function StoredWeapon({ top, weapon }: { top: number; weapon?: Weapon }) {
  const y = (v: number) => v + top;
  const secondary = weapon?.category === 'Secondary';
  return (
    <g>
      <Txt x={290.1} y={y(613.8)} size={9.1} weight={900} tone="title">INVENTORY WEAPON</Txt>
      <Hands x={467.8} y={y(604.1)} scale={0.583} light used={handsUsed(secondary ? undefined : weapon, secondary ? weapon : undefined)} />
      <rect className={`s-paper o-frame${weapon && !secondary ? ' is-marked' : ''}`} x={499.1} y={y(606.6)} width={7.2} height={7.2} />
      <Txt x={508.8} y={y(612.7)} size={6.4} weight={800} tone="muted">PRIMARY</Txt>
      <rect className={`s-paper o-frame${secondary ? ' is-marked' : ''}`} x={549} y={y(606.6)} width={7.2} height={7.2} />
      <Txt x={558.7} y={y(612.7)} size={6.4} weight={800} tone="muted">SECONDARY</Txt>
      <Dots x={415.5} y0={y(622.1)} y1={y(629.4)} tone="rule" />
      <Dots x={511.1} y0={y(622.1)} y1={y(629.4)} tone="rule" />
      <WriteLine x0={290.2} x1={595.5} y={y(630.5)} />
      <Value x={294} base={y(631.9)} w={119}>{weapon?.name}</Value>
      <Value x={417} base={y(631.9)} w={92}>{weapon && [weapon.trait, weapon.range].filter(Boolean).join(' ')}</Value>
      <Value x={513} base={y(631.9)} w={80}>{weapon && damageLine(weapon)}</Value>
      <Txt x={294.1} y={y(641.4)} size={6.4} weight={800}>NAME</Txt>
      <Txt x={416.2} y={y(641.4)} size={6.4} weight={800}>TRAIT &amp; RANGE</Txt>
      <Txt x={513.1} y={y(641.4)} size={6.4} weight={800}>DAMAGE DICE &amp; TYPE</Txt>
      <FeatureLines top={y(656.1)} text={weapon?.feature} />
    </g>
  );
}

const INVENTORY_LINES = 5;

/** One entry per line while they fit; past that, the two shortest neighbours share a line until every entry is on the sheet. */
function packLines(entries: string[], count: number): string[] {
  const lines = [...entries];
  while (lines.length > count) {
    let at = 0;
    for (let i = 1; i < lines.length - 1; i++) {
      if (lines[i].length + lines[i + 1].length < lines[at].length + lines[at + 1].length) at = i;
    }
    lines.splice(at, 2, `${lines[at]}, ${lines[at + 1]}`);
  }
  return [...lines, ...Array(count - lines.length).fill('')];
}

/** The starting kit, item by item, then the starting choices and the loot, on the sheet's five inventory lines. */
function inventoryLines(c: Character, cls?: DhClass, empty = false): string[] {
  // The craft is the player's own words, so it keeps any article they wrote.
  const kit = c.className && !empty ? [...[...keptKit(c, cls), c.potion, c.classItem].map(itemName), c.craft, c.useIkonis ? IKONIS_RULES.tether : ''].filter(Boolean) : [];
  return packLines([...kit, ...c.loot], INVENTORY_LINES);
}

/* ── Class features and the play aids printed beside them ── */

const DIE_SIZES = ['d4', 'd6', 'd8', 'd10'];

interface Extras { head?: ReactNode; side?: ReactNode; after?: ReactNode }

function Rings({ label, total, marked, onSet }: { label: string; total: number; marked: number; onSet?: (n: number) => void }) {
  return (
    <span className="cs-rings" role="group" aria-label={label}>
      {Array.from({ length: total }, (_, i) =>
        onSet ? (
          <button key={i} type="button" className={`cs-ring${i < marked ? ' is-marked' : ''}`} aria-label={`${label} ${i + 1}`} aria-pressed={i < marked} onClick={() => onSet(i < marked ? i : i + 1)} />
        ) : (
          <span key={i} className={`cs-ring${i < marked ? ' is-marked' : ''}`} />
        ),
      )}
    </span>
  );
}

function featureExtras(name: string, c: Character, onMark?: PageProps['onMark'], onChoose?: PageProps['onChoose']): Extras {
  switch (name) {
    case 'Unstoppable':
      return { side: <span className="cs-die cs-die--one" aria-label="Unstoppable Die" /> };
    case 'Prayer Dice':
      return { side: <span className="cs-dice" aria-label="Prayer Dice">{Array.from({ length: 6 }, (_, i) => <span key={i} className="cs-die" />)}</span> };
    case 'Combo Strike':
      return {
        head: (
          <span className="cs-die-sizes" aria-label="Combo Die">
            {DIE_SIZES.map((d, i) => <span key={d}>{d}<span className={`cs-ring${i === 0 ? ' is-current' : ''}`} /></span>)}
          </span>
        ),
      };
    case "Patron's Pact": {
      const lines = (
        <>
          <span><span className="cs-pair__label">Patron's Name</span><span className="cs-pair__line">{c.patronName}</span></span>
          <span><span className="cs-pair__label">Sphere of Influence</span><span className="cs-pair__line">{c.sphere}</span></span>
        </>
      );
      // On screen the lines open the patron dialog; printed, they are plain write-in lines.
      return {
        after: onChoose
          ? <button type="button" className="cs-pair cs-edit" aria-label="Name your patron" title="Name your patron" onClick={() => onChoose('patron')}>{lines}<GearMark label="Name your patron" /></button>
          : <span className="cs-pair">{lines}</span>,
      };
    }
    case 'Favor':
      return { head: <Rings label="Favor" total={6} marked={markCount(c, 'favor')} onSet={onMark && ((n) => onMark('favor', n))} /> };
    case 'Strange Patterns':
      return { side: <span className="cs-short" aria-label="Strange Patterns number">{c.strangePattern}</span> };
    default:
      return {};
  }
}

const CHART = /\s(?=\d+(?:-\d+)?: )/;

/** Splits "...the chart below. 1-3: A. 4-5: B. 6: C." into its text and chart rows. */
export function splitRollChart(text: string): { intro: string; rows: Array<[string, string]> } {
  if (!/\s1-\d+: /.test(text)) return { intro: text, rows: [] };
  const [intro, ...rows] = text.split(CHART);
  return { intro, rows: rows.map((r) => [r.slice(0, r.indexOf(': ')), r.slice(r.indexOf(': ') + 2)]) };
}

/** One class feature with its play aids; also used by the multiclass module. */
export function ClassFeatureBlock({ feature, c, onMark, onChoose }: { feature: ClassFeature; c: Character; onMark?: PageProps['onMark']; onChoose?: PageProps['onChoose'] }) {
  const extras = featureExtras(feature.name ?? '', c, onMark, onChoose);
  const { intro, rows } = splitRollChart(feature.description ?? '');
  return (
    <div className="cs-feature">
      {feature.name && <h4 className="cs-feature__name">{feature.name}{extras.head}</h4>}
      {extras.side ? (
        <div className="cs-feature__body">
          <p>{emphasize(intro)}</p>
          {extras.side}
        </div>
      ) : (
        <p>{emphasize(intro)}</p>
      )}
      {rows.length > 0 && (
        <table className="cs-chart" aria-label={feature.name}>
          <thead><tr><th>Roll</th><th>Effect</th></tr></thead>
          <tbody>{rows.map(([roll, effect]) => <tr key={roll}><td>{roll}</td><td>{effect}</td></tr>)}</tbody>
        </table>
      )}
      {extras.after}
    </div>
  );
}
